// Per-state instructions for filing the quarterly IFTA return with the base
// jurisdiction. Links point to official state government sites; researched
// August 2026 via each state's website.

export type IftaStateInstructions = {
  code: string;
  name: string;
  agency: string;
  portalName: string;
  portalUrl: string;
  infoUrl: string;
  steps: string[];
  notes?: string;
};

export const IFTA_FILING_INSTRUCTIONS: IftaStateInstructions[] = [
  {
    "code": "AL",
    "name": "Alabama",
    "agency": "Alabama Department of Revenue (Motor Vehicle Division)",
    "portalName": "Alabama Motor Carrier (AMC) System",
    "portalUrl": "https://amcmod.mvtrip.alabama.gov/ALMcsWeb/SignOn",
    "infoUrl": "https://www.revenue.alabama.gov/motor-vehicle/irp-ifta-information/",
    "steps": [
      "Log in to the Alabama Motor Carrier (AMC) System using your 9-digit taxpayer ID (FEIN or SSN, no dashes) and password",
      "Select the IFTA quarterly tax return for the quarter from your customer dashboard",
      "Enter miles traveled and gallons purchased for each jurisdiction and submit the return",
      "Pay any balance due electronically and reprint a copy of the filed return from the Reprint menu"
    ],
    "notes": "The AMC login user ID is your 9-digit FEIN or SSN with no dashes; e-filing through AMC is free."
  },
  {
    "code": "AZ",
    "name": "Arizona",
    "agency": "Arizona Department of Transportation, Motor Vehicle Division",
    "portalName": "AZ MVD Now",
    "portalUrl": "https://azmvdnow.gov",
    "infoUrl": "https://azdot.gov/mvd/services/motor-carrier-services/motor-carrier-licensing/ifta-tax-filing-online-service",
    "steps": [
      "Log in to AZ MVD Now with the account tied to your company email",
      "Open the IFTA Quarterly Tax Filing service for the quarter",
      "Enter miles and fuel gallons per jurisdiction; the system calculates the tax due automatically",
      "Submit the report and pay any balance due online"
    ],
    "notes": "You must first register an AZ MVD Now account with the email address associated with the company before you can e-file."
  },
  {
    "code": "AR",
    "name": "Arkansas",
    "agency": "Arkansas Department of Finance and Administration",
    "portalName": "Arkansas Motor Carrier System (AMCS)",
    "portalUrl": "https://amcs.arkansas.gov/ARMcsWeb/SignOn",
    "infoUrl": "https://www.dfa.arkansas.gov/office/taxes/excise-tax-administration/motor-fuel-tax/",
    "steps": [
      "Log in to the Arkansas Motor Carrier System with your IFTA account credentials",
      "Open the IFTA quarterly tax report and enter total miles, taxable miles, and fuel purchased for each jurisdiction",
      "Submit the report and pay any tax due online by credit card or e-check"
    ]
  },
  {
    "code": "CA",
    "name": "California",
    "agency": "California Department of Tax and Fee Administration (CDTFA)",
    "portalName": "CDTFA Online Services",
    "portalUrl": "https://onlineservices.cdtfa.ca.gov/_/",
    "infoUrl": "https://www.cdtfa.ca.gov/taxes-and-fees/ifta-ciudft-di-license-onlinefiling.htm",
    "steps": [
      "Log in to CDTFA Online Services with your username and password",
      "Open the IFTA quarterly return for the filing period on your IFTA account",
      "Enter miles and gallons per jurisdiction, or upload CDTFA's Excel jurisdiction schedule template, then submit",
      "Select Make a Payment to pay any balance due by ACH debit or credit card"
    ],
    "notes": "Online filing is required, and a return must be filed every quarter even if the fleet did not operate."
  },
  {
    "code": "CO",
    "name": "Colorado",
    "agency": "Colorado Department of Revenue",
    "portalName": "Revenue Online",
    "portalUrl": "https://www.colorado.gov/revenueonline/",
    "infoUrl": "https://tax.colorado.gov/IFTA",
    "steps": [
      "Log in to Colorado Revenue Online and open your IFTA account",
      "Start the IFTA tax return for the quarter and enter miles and gallons for each jurisdiction",
      "Submit the return and pay any balance due electronically"
    ],
    "notes": "Colorado is phasing in mandatory electronic filing; carriers required to e-file face a penalty of the greater of $50 or 5% of tax due for filing on paper."
  },
  {
    "code": "CT",
    "name": "Connecticut",
    "agency": "Connecticut Department of Revenue Services",
    "portalName": "myconneCT",
    "portalUrl": "https://drs.ct.gov/eservices/_/",
    "infoUrl": "https://portal.ct.gov/drs/ifta/connecticut-ifta",
    "steps": [
      "Log in to myconneCT with your username and password",
      "Open your IFTA account and select the quarterly return for the period",
      "Enter miles driven and fuel purchased in each jurisdiction and submit",
      "Pay any balance due electronically through myconneCT"
    ],
    "notes": "The same myconneCT account is also used to renew the IFTA license and order additional decals."
  },
  {
    "code": "DE",
    "name": "Delaware",
    "agency": "Delaware Division of Motor Vehicles",
    "portalName": "Delaware Motor Carrier Services System (Motor Carrier Online)",
    "portalUrl": "https://services.dmv.de.gov/VehicleServices/MC/index.shtml?dc=iftaDefault",
    "infoUrl": "https://dmv.de.gov/services/MotorCarrier/index.shtml",
    "steps": [
      "Log in to the Delaware DMV Motor Carrier online system with your carrier account",
      "Open the International Fuel Use Tax Return for the quarter and enter miles and gallons per jurisdiction",
      "Submit the return, filing a zero report if you had no IFTA travel that quarter",
      "Pay any balance due online by credit card or e-check"
    ],
    "notes": "Returns are accepted only online or by mail, a zero report is required for inactive quarters, and portal passwords expire every 90 days."
  },
  {
    "code": "FL",
    "name": "Florida",
    "agency": "Florida Department of Highway Safety and Motor Vehicles",
    "portalName": "Interstate Carrier Filing System (ICFS)",
    "portalUrl": "https://icfs.flhsmv.gov/FLEnterprise/",
    "infoUrl": "https://www.flhsmv.gov/driver-licenses-id-cards/commercial-motor-vehicle-drivers/international-fuel-tax-agreement/e-filing/",
    "steps": [
      "Log in to the Interstate Carrier Filing System (ICFS) with your carrier account",
      "Select the IFTA tax return transaction for the quarter",
      "Enter miles traveled and fuel purchased for each jurisdiction and submit, even if the fleet only ran in Florida or did not travel",
      "Pay any balance due online by credit card"
    ],
    "notes": "First-time e-filers must submit an E-File Request form to FLHSMV to get ICFS access before they can file online."
  },
  {
    "code": "GA",
    "name": "Georgia",
    "agency": "Georgia Department of Revenue",
    "portalName": "Georgia Tax Center (GTC)",
    "portalUrl": "https://gtc.dor.ga.gov",
    "infoUrl": "https://dor.georgia.gov/taxes/international-fuel-tax-agreement/ifta-quarterly-fuel-tax-return-instructions",
    "steps": [
      "Log in to the Georgia Tax Center and open your IFTA account, then click View Returns.",
      "Click File Return next to the quarter you are filing and say whether you operated that period.",
      "Enter your miles and fuel gallons by jurisdiction on the IFTA Tax Detail page.",
      "Review the summary, submit the return, and pay any tax due."
    ],
    "notes": "Your business must be registered on Georgia Tax Center with an IFTA account before you can file."
  },
  {
    "code": "ID",
    "name": "Idaho",
    "agency": "Idaho State Tax Commission",
    "portalName": "Taxpayer Access Point (TAP)",
    "portalUrl": "https://tax.idaho.gov/gototap",
    "infoUrl": "https://tax.idaho.gov/taxes/product-excise-taxes/fuels-taxes-and-fees/consumer-fuels/ifta-licenses/ifta-reporting/",
    "steps": [
      "Log in to TAP and open the IFTA section for your account.",
      "Start a new Form 3150 quarterly report for the filing period.",
      "Enter total miles and gallons, then miles and tax-paid gallons by jurisdiction; TAP calculates the tax.",
      "Submit by 11:59 p.m. Mountain Time on the due date and schedule your payment."
    ],
    "notes": "Idaho requires IFTA returns to be filed electronically through TAP, and you must file even with no travel or tax due."
  },
  {
    "code": "IL",
    "name": "Illinois",
    "agency": "Illinois Department of Revenue",
    "portalName": "MyTax Illinois",
    "portalUrl": "https://mytax.illinois.gov/",
    "infoUrl": "https://tax.illinois.gov/research/taxinformation/motorfuel/mfut.html",
    "steps": [
      "Log in to your MyTax Illinois account and open your Motor Fuel Use Tax (IFTA) account.",
      "Start Form MFUT-15 for the quarter and enter total miles in IFTA and non-IFTA jurisdictions.",
      "Enter miles driven and gallons purchased per jurisdiction, or import them from a CSV file.",
      "Submit the return and pay any tax due by the last day of the month after the quarter ends."
    ],
    "notes": "Illinois has an electronic mandate: the MFUT-15 must be filed through MyTax Illinois."
  },
  {
    "code": "IN",
    "name": "Indiana",
    "agency": "Indiana Department of Revenue, Motor Carrier Services",
    "portalName": "Fuel Tax System",
    "portalUrl": "https://motorcarrier.dor.in.gov/loginHome.html",
    "infoUrl": "https://www.in.gov/dor/motor-carrier-services/fuel-tax/",
    "steps": [
      "Log in to the Fuel Tax System with your Motor Carrier Services account.",
      "Open the IFTA quarterly tax return for the current period.",
      "Enter miles traveled and fuel purchased by jurisdiction and submit the return.",
      "Pay any tax due online through the portal."
    ],
    "notes": "Indiana requires carriers to manage IFTA transactions online, and a return is due even if no fuel was used."
  },
  {
    "code": "IA",
    "name": "Iowa",
    "agency": "Iowa Department of Transportation (Motor Vehicle Division)",
    "portalName": "Iowa DOT Motor Carrier online system",
    "portalUrl": "https://iowadot.gov/mvd/motorcarriers",
    "infoUrl": "https://iowadot.gov/motor-carriers/ifta-international-fuel-tax-agreement",
    "steps": [
      "Log in to the Iowa DOT motor carrier system with your Client ID and password.",
      "Choose IFTA from the applications menu, then select Quarterly Tax Return.",
      "Enter miles traveled and fuel purchased for each jurisdiction and submit.",
      "Pay any balance due before the quarterly deadline."
    ],
    "notes": "You must file a zero return even for quarters with no IFTA travel; contact Iowa DOT to get online account credentials first."
  },
  {
    "code": "KS",
    "name": "Kansas",
    "agency": "Kansas Department of Revenue",
    "portalName": "KDOR Customer Service Center",
    "portalUrl": "https://www.kdor.ks.gov/Apps/KCSC/login.aspx",
    "infoUrl": "https://www.ksrevenue.gov/mfifta.html",
    "steps": [
      "Log in to the KDOR Customer Service Center with your Kansas ID and IFTA account.",
      "Open the IFTA quarterly filing and enter miles and fuel gallons per jurisdiction; the system calculates tax, interest, and penalties.",
      "Submit the return, then pay by electronic check or print a mail-in payment voucher."
    ],
    "notes": "Online filing is only available to carriers licensed and in good standing."
  },
  {
    "code": "KY",
    "name": "Kentucky",
    "agency": "Kentucky Transportation Cabinet, Division of Motor Carriers",
    "portalName": "Motor Carrier Connect",
    "portalUrl": "https://ky.motorcarrierconnect.com/Dashboard",
    "infoUrl": "https://drive.ky.gov/motor-carriers/Pages/IFTA.aspx",
    "steps": [
      "Log in to Motor Carrier Connect with your KYTC motor carrier account.",
      "Open the IFTA quarterly return for the period and enter miles and fuel purchased by jurisdiction.",
      "Submit the return and pay any tax due online.",
      "File a zero return for quarters with no travel to avoid penalties."
    ],
    "notes": "IFTA returns must be filed online, and split-period returns are required when Kentucky's tax rate changes mid-quarter."
  },
  {
    "code": "LA",
    "name": "Louisiana",
    "agency": "Louisiana Department of Revenue",
    "portalName": "Louisiana Taxpayer Access Point (LaTAP)",
    "portalUrl": "https://revenue.louisiana.gov/LaTAP/",
    "infoUrl": "https://revenue.louisiana.gov/contact-us/business-taxpayer-email-forms/international-fuel-tax-agreement-ifta-inquiries/",
    "steps": [
      "Log in to your LaTAP account and select your IFTA account.",
      "Open the return for the quarter and enter miles traveled and fuel purchased in each jurisdiction.",
      "Submit the return and pay any tax due by the last day of the month after the quarter ends."
    ],
    "notes": "You must register for a LaTAP account (and hold an LDR-issued IFTA license) before you can e-file."
  },
  {
    "code": "ME",
    "name": "Maine",
    "agency": "Maine Bureau of Motor Vehicles (Secretary of State)",
    "portalName": "Maine IFTA Online E-File Service",
    "portalUrl": "https://www.maine.gov/ifta/",
    "infoUrl": "https://www.maine.gov/sos/bmv/vehicles/commercial-vehicles-motor-carrier-services/fuel-tax-licensing-and-reporting",
    "steps": [
      "Request online access to your carrier account and return the signed IFTA online account application to BMV.",
      "Log in to Maine's IRP/IFTA e-file system from the maine.gov IFTA page.",
      "Enter your quarterly miles and fuel by jurisdiction and file the return.",
      "Pay any balance due online by card if your account is in good standing."
    ],
    "notes": "Online filing requires a one-time access request with a company officer's signature before BMV activates your account."
  },
  {
    "code": "MD",
    "name": "Maryland",
    "agency": "Comptroller of Maryland (Motor Fuel Tax Unit)",
    "portalName": "Maryland Motor Carrier Connect",
    "portalUrl": "https://md.motorcarrierconnect.com/",
    "infoUrl": "https://taxes.marylandtaxes.gov/Business_Taxes/Business_Tax_Types/Motor_Fuel_Tax/Tax_Information/IFTA",
    "steps": [
      "Log in to Maryland Motor Carrier Connect with your carrier account.",
      "Open the IFTA return for the quarter and enter miles and fuel purchased by jurisdiction.",
      "Submit the return and pay any tax due online."
    ],
    "notes": "Maryland replaced its old IFTA e-file site with the state-run Motor Carrier Connect system, which is hosted on a vendor domain rather than .gov."
  },
  {
    "code": "MA",
    "name": "Massachusetts",
    "agency": "Massachusetts Department of Revenue (DOR)",
    "portalName": "MassTaxConnect",
    "portalUrl": "https://mtc.dor.state.ma.us/mtc/_/",
    "infoUrl": "https://www.mass.gov/info-details/international-fuels-tax-agreement-for-motor-carriers-ifta",
    "steps": [
      "Log in to your MassTaxConnect account and open your IFTA account.",
      "Select the quarter and enter total miles and gallons for each state or province you ran in.",
      "Review the tax the system calculates, then submit the return.",
      "Pay any balance due electronically before the quarterly deadline."
    ],
    "notes": "You must file a zero return even for quarters with no travel; late filing costs $50 or 10% of tax due, whichever is greater."
  },
  {
    "code": "MI",
    "name": "Michigan",
    "agency": "Michigan Department of Treasury",
    "portalName": "Michigan Motor Carrier Connect",
    "portalUrl": "https://mi.motorcarrierconnect.com/",
    "infoUrl": "https://www.michigan.gov/taxes/business-taxes/ifta",
    "steps": [
      "Log in to Michigan Motor Carrier Connect with your IFTA user name and password.",
      "Open the quarterly return and enter miles driven and fuel bought in each jurisdiction.",
      "Submit the return and pay any tax due through the system by the deadline."
    ],
    "notes": "Michigan moved carriers from the old IPC portal to Motor Carrier Connect; a return is required every quarter even with no activity."
  },
  {
    "code": "MN",
    "name": "Minnesota",
    "agency": "Minnesota Department of Public Safety, Driver and Vehicle Services (DVS)",
    "portalName": "MnDRIVE e-Services for Business",
    "portalUrl": "https://onlineservices.dps.mn.gov/EServices/_/",
    "infoUrl": "https://dps.mn.gov/divisions/dvs/irp-ifta/Pages/default.aspx",
    "steps": [
      "Log in to DVS e-Services for Business (MnDRIVE).",
      "Choose File an IFTA Quarterly Return and enter miles and gallons per jurisdiction.",
      "Submit the return and pay any tax due online."
    ],
    "notes": "Missing a quarterly filing can suspend both your IRP registration and IFTA license, so file even for zero-mileage quarters."
  },
  {
    "code": "MS",
    "name": "Mississippi",
    "agency": "Mississippi Department of Revenue",
    "portalName": "Taxpayer Access Point (TAP)",
    "portalUrl": "https://tap.dor.ms.gov/_/",
    "infoUrl": "https://www.dor.ms.gov/business/interstate-commerical-vehicles",
    "steps": [
      "Log in to your TAP account and open your IFTA account.",
      "Enter quarterly miles and fuel by jurisdiction manually, or upload the data with the approved Excel spreadsheet.",
      "Submit the return and pay any tax due by EFT or card."
    ],
    "notes": "TAP accepts either keyed-in data or an Excel upload, but only the current TAP spreadsheet format works."
  },
  {
    "code": "MO",
    "name": "Missouri",
    "agency": "Missouri Department of Transportation (MoDOT) Motor Carrier Services",
    "portalName": "MoDOT Carrier Express",
    "portalUrl": "https://mcs.modot.mo.gov/",
    "infoUrl": "https://www.modot.org/file-ifta-return",
    "steps": [
      "Log in to MoDOT Carrier Express with your user ID and password.",
      "Go to Credentialing, then Services, then IFTA, then Tax Return, and choose File/Amend.",
      "Enter quarterly miles and fuel by jurisdiction and submit the return.",
      "Pay any balance due online by the quarterly deadline."
    ],
    "notes": "In Missouri IFTA is run by the Department of Transportation, not the Department of Revenue, and zero returns are required."
  },
  {
    "code": "MT",
    "name": "Montana",
    "agency": "Montana Department of Transportation (MDT) Motor Carrier Services",
    "portalName": "ePART",
    "portalUrl": "https://mdt.mt.gov/business/fueltax/ifta.aspx",
    "infoUrl": "https://www.mdt.mt.gov/business/mcs/default.aspx",
    "steps": [
      "Request an ePART online account from MDT; credentials are emailed in 1 to 3 business days.",
      "Log in to ePART from the MDT IFTA page and open the quarterly tax return.",
      "Enter miles and fuel by jurisdiction, then submit and pay online."
    ],
    "notes": "MDT never texts carriers about filing (only email and mail), and a return is required each quarter even with no activity."
  },
  {
    "code": "NE",
    "name": "Nebraska",
    "agency": "Nebraska Department of Motor Vehicles (Motor Carrier Services)",
    "portalName": "Nebraska Motor Carrier Information System (MCIS)",
    "portalUrl": "https://mcis.nebraska.gov/NEEnterprise/",
    "infoUrl": "https://dmv.nebraska.gov/mc/ifta",
    "steps": [
      "Log in to the Nebraska DMV's IRP and IFTA online portal (MCIS).",
      "Enter total miles and fuel for the quarter, broken down by jurisdiction, and submit the return.",
      "Pay any balance due online by e-check or Visa/MasterCard.",
      "File every quarter, even if your trucks never left Nebraska."
    ]
  },
  {
    "code": "NV",
    "name": "Nevada",
    "agency": "Nevada Department of Motor Vehicles (Motor Carrier Division)",
    "portalName": "Nevada Motor Carrier Connect",
    "portalUrl": "https://nv.motorcarrierconnect.com/Identity/Authentication/Login",
    "infoUrl": "https://dmv.nv.gov/mcexisting.htm",
    "steps": [
      "Request online access with form MC 106 (or call the Motor Carrier Division) if you don't have an account yet.",
      "Log in to Nevada Motor Carrier Connect and open the IFTA quarterly return.",
      "Enter miles and fuel by jurisdiction, submit the return, and pay any tax due online."
    ],
    "notes": "A return is required even for zero-mile quarters, with a brief explanation of why no miles were traveled."
  },
  {
    "code": "NH",
    "name": "New Hampshire",
    "agency": "New Hampshire Department of Safety, Road Toll Bureau (Motor Carrier Services)",
    "portalName": "NH Motor Carrier Connect",
    "portalUrl": "https://nh.motorcarrierconnect.com/",
    "infoUrl": "https://www.administration.dos.nh.gov/road-toll",
    "steps": [
      "Log in to the NH Motor Carrier Connect portal.",
      "Open the IFTA quarterly return and enter miles and fuel for each jurisdiction.",
      "Submit the return and pay any tax due online by the quarterly deadline."
    ],
    "notes": "NH replaced its old iftaipc.com filing site with Motor Carrier Connect, and IFTA services moved to the DMV's new Motor Carrier Services team in Concord in 2025."
  },
  {
    "code": "NJ",
    "name": "New Jersey",
    "agency": "New Jersey Motor Vehicle Commission (Motor Carrier Services)",
    "portalName": "NJMVC Motor Carrier Service online system",
    "portalUrl": "https://mvcappwintelirp.state.nj.us/njWeb/login.do",
    "infoUrl": "https://www.nj.gov/mvc/business/ifta.htm",
    "steps": [
      "Log in with your customer number and PIN from your most recent NJMVC statement.",
      "Open the IFTA tab, choose Quarterly Tax Return, and select the reporting period.",
      "Enter total miles and fuel, then the breakdown by jurisdiction, and file the return.",
      "Pay the invoice under the Payment tab by credit card or online checking."
    ],
    "notes": "Your login PIN is printed on your most recent NJMVC statement; call (609) 633-9400 option 1 if you don't have it."
  },
  {
    "code": "NM",
    "name": "New Mexico",
    "agency": "New Mexico Taxation and Revenue Department, Motor Vehicle Division",
    "portalName": "Taxpayer Access Point (TAP)",
    "portalUrl": "https://tap.state.nm.us/tap/_/",
    "infoUrl": "https://www.mvd.newmexico.gov/commercial/commercial-vehicles/fuels-tax/",
    "steps": [
      "Log in to your TAP account linked to your Commercial Vehicle Bureau IFTA account.",
      "Open the IFTA quarterly return and enter miles and gallons for each jurisdiction.",
      "Submit the return and pay any tax due online by the quarterly deadline."
    ],
    "notes": "The same TAP account also handles New Mexico's separate Weight Distance Tax return, which most carriers must file in addition to IFTA."
  },
  {
    "code": "NY",
    "name": "New York",
    "agency": "New York State Department of Taxation and Finance",
    "portalName": "IFTA Web File (Business Online Services)",
    "portalUrl": "https://www.tax.ny.gov/online/bus.htm",
    "infoUrl": "https://www.tax.ny.gov/bus/ifta/fuel.htm",
    "steps": [
      "Log in to (or create) your Business Online Services account at tax.ny.gov.",
      "Choose IFTA Web File and enter the quarter's miles and fuel by jurisdiction; tax due is calculated automatically.",
      "Submit the return and pay online by the quarterly deadline."
    ],
    "notes": "Quarterly returns go through IFTA Web File; the separate OSCAR system is only for licenses, decals, and other credentials."
  },
  {
    "code": "NC",
    "name": "North Carolina",
    "agency": "North Carolina Department of Revenue (Excise Tax Division)",
    "portalName": "Motor Carrier IFTA/Intrastate eFile",
    "portalUrl": "https://eservices.dor.nc.gov/ifta/",
    "infoUrl": "https://www.ncdor.gov/taxes-forms/motor-carrier-iftaintrastate-efile",
    "steps": [
      "Total your fleet's miles traveled and gallons purchased in each state for the quarter.",
      "Log in to the NCDOR IFTA/Intrastate eFile system with your account and access code.",
      "Enter miles and fuel for each jurisdiction and submit the return.",
      "Pay any tax due by bank draft or debit/credit card by the quarterly deadline."
    ],
    "notes": "Electronic filing is mandatory in NC, but amended returns cannot be e-filed and must go in on paper."
  },
  {
    "code": "ND",
    "name": "North Dakota",
    "agency": "North Dakota Department of Transportation (Motor Carrier Section)",
    "portalName": "North Dakota Motor Carrier Online Services",
    "portalUrl": "https://apps.nd.gov/dot/cvisn/login.htm",
    "infoUrl": "https://www.dot.nd.gov/motor-vehicle/international-registration-plans-ifta-irp-and-ucr",
    "steps": [
      "Register for NDDOT Motor Carrier Online Services if you have not already.",
      "Log in and open the IFTA quarterly tax return for the reporting period.",
      "Enter miles traveled and fuel purchased in each jurisdiction and submit.",
      "Pay any balance online by the last day of the month after the quarter ends."
    ],
    "notes": "First-time online users must register and wait for an authorization code that NDDOT mails the next business day."
  },
  {
    "code": "OH",
    "name": "Ohio",
    "agency": "Ohio Department of Taxation",
    "portalName": "Ohio Business Gateway",
    "portalUrl": "https://gateway.ohio.gov",
    "infoUrl": "https://tax.ohio.gov/excise/international_fuel_tax_agreement_ifta.aspx",
    "steps": [
      "Log in to the Ohio Business Gateway with your OH|ID account.",
      "Open your IFTA account and start the quarterly return.",
      "Enter miles traveled and fuel purchased for each state.",
      "Submit the return and pay any balance by ACH debit."
    ],
    "notes": "Sign-in goes through the statewide OH|ID single sign-on rather than a separate tax login."
  },
  {
    "code": "OK",
    "name": "Oklahoma",
    "agency": "Oklahoma Corporation Commission, Transportation Division",
    "portalName": "OCC IRP/IFTA Online System",
    "portalUrl": "https://apps.occ.ok.gov/IRPIFTA/",
    "infoUrl": "https://oklahoma.gov/occ/divisions/transportation/trucking/occ-transportation-division-ifta.html",
    "steps": [
      "Download the current quarter's IFTA return form from the OCC website.",
      "Fill in miles traveled and fuel purchased for each state.",
      "Mail the completed form with any payment to the address on the form, or deliver it to the OCC office.",
      "File a zero return even if you had no activity that quarter."
    ],
    "notes": "Quarterly returns are still primarily paper forms mailed to the OCC; the online system mainly handles registrations and renewals, and the posted forms cover diesel only (contact the OCC for other fuels)."
  },
  {
    "code": "OR",
    "name": "Oregon",
    "agency": "Oregon Department of Transportation, Commerce and Compliance Division",
    "portalName": "Oregon Trucking Online",
    "portalUrl": "https://www.oregontruckingonline.com",
    "infoUrl": "https://www.oregon.gov/odot/mct/pages/interstate-operations-ifta.aspx",
    "steps": [
      "Log in to Oregon Trucking Online with your account number and PIN.",
      "Open the IFTA tax return (form 9740) for the quarter; tax rates and calculations fill in automatically.",
      "Enter miles and gallons for each state.",
      "Submit and pay by Visa, Mastercard, or direct debit."
    ],
    "notes": "Oregon charges a separate weight-mile tax instead of a fuel tax, so the IFTA return settles fuel tax owed to other states; you must file every quarter even with Oregon-only or no operations."
  },
  {
    "code": "PA",
    "name": "Pennsylvania",
    "agency": "Pennsylvania Department of Revenue",
    "portalName": "myPATH",
    "portalUrl": "https://mypath.pa.gov",
    "infoUrl": "https://www.pa.gov/agencies/revenue/resources/tax-types-and-information/motor-and-alternative-fuel-taxes/motor-carriers-road-tax",
    "steps": [
      "Log in to your myPATH profile (register a username and password first if needed).",
      "Select your IFTA account and the filing period, then choose File Now.",
      "Enter miles and gallons for each state, or upload the data as a CSV file.",
      "Submit the return and pay electronically."
    ],
    "notes": "Pennsylvania administers IFTA together with its Motor Carriers Road Tax (MCRT), and both are handled through the same myPATH account."
  },
  {
    "code": "RI",
    "name": "Rhode Island",
    "agency": "Rhode Island Division of Taxation",
    "portalName": "Rhode Island Taxpayer Portal",
    "portalUrl": "https://taxportal.ri.gov",
    "infoUrl": "https://tax.ri.gov/tax-sections/sales-excise-taxes/international-fuel-tax-agreement-ifta",
    "steps": [
      "Register on the RI Taxpayer Portal and link your IFTA account using the PIN letter mailed by the Division of Taxation.",
      "Open the RI-IFTA-100 return for the quarter.",
      "Enter miles and gallons for each state.",
      "Submit the return and pay any tax due at the same time."
    ],
    "notes": "Filing and paying on the portal has been mandatory for all IFTA filers since April 1, 2024, and linking your account requires a mailed PIN."
  },
  {
    "code": "SC",
    "name": "South Carolina",
    "agency": "South Carolina Department of Motor Vehicles (SCDMV), Motor Carrier Services",
    "portalName": "SCDMV Motor Carrier Services Portal",
    "portalUrl": "https://www.scdmvonline.com/scmcs/",
    "infoUrl": "https://dmv.sc.gov/Business-Customers/Motor-Carriers/International-Fuel-Tax-Agreement/IFTA-Tax-Returns",
    "steps": [
      "Submit form MC-26 (Motor Carrier Web Entry Access Application) once to get your portal user ID and password.",
      "Log in to the MCS online portal.",
      "Complete the IFTA-3 return with total miles traveled and fuel purchased for the quarter.",
      "Submit the return and pay any tax due."
    ],
    "notes": "IFTA is run by the DMV rather than the tax agency, and online filing requires an approved MC-26 web access application first."
  },
  {
    "code": "SD",
    "name": "South Dakota",
    "agency": "South Dakota Department of Revenue, Motor Carrier Services",
    "portalName": "Motor Carrier Connect",
    "portalUrl": "https://sd.motorcarrierconnect.com",
    "infoUrl": "https://sdtruckinfo.sd.gov/interstate-carriers/international-fuel-tax-agreement-ifta/",
    "steps": [
      "Log in to your Motor Carrier Connect account.",
      "Open the IFTA quarterly return for the period.",
      "Enter miles and gallons for each state.",
      "Submit the return and pay online."
    ],
    "notes": "Electronic filing has been required since July 2020, and a zero return is due even with no activity; Motor Carrier Connect replaced the old Interstate Online system in 2022."
  },
  {
    "code": "TN",
    "name": "Tennessee",
    "agency": "Tennessee Department of Revenue",
    "portalName": "TNTAP (Tennessee Taxpayer Access Point)",
    "portalUrl": "https://tntap.tn.gov/eservices/_/",
    "infoUrl": "https://www.tn.gov/revenue/motor-carrier/international-fuel-tax-agreement.html",
    "steps": [
      "Log in to TNTAP (create a logon and link your IFTA account the first time).",
      "Select your IFTA account and the quarter to file.",
      "Enter miles and gallons for each state.",
      "Submit the return and pay any tax due, both by the last day of the month after the quarter ends."
    ]
  },
  {
    "code": "TX",
    "name": "Texas",
    "agency": "Texas Comptroller of Public Accounts",
    "portalName": "Webfile (eSystems)",
    "portalUrl": "https://security.app.cpa.state.tx.us/",
    "infoUrl": "https://comptroller.texas.gov/taxes/fuels/ifta.php",
    "steps": [
      "Log in to Webfile with your eSystems account and Webfile number.",
      "Enter each jurisdiction's miles traveled and gallons of fuel purchased for the quarter.",
      "Review the calculated tax due or credit, then submit the return and pay electronically."
    ],
    "notes": "Webfile returns must be submitted by 11:59 p.m. Central Time on the due date; late returns get a $50 penalty."
  },
  {
    "code": "UT",
    "name": "Utah",
    "agency": "Utah State Tax Commission (Motor Carrier Services)",
    "portalName": "Taxpayer Access Point (TAP)",
    "portalUrl": "https://tap.utah.gov/",
    "infoUrl": "https://tax.utah.gov/business/other-taxes/ifta-sfu/",
    "steps": [
      "Sign in to your TAP account at tap.utah.gov.",
      "Fill in the state's approved TC-922 spreadsheet template with your quarterly miles and fuel by jurisdiction.",
      "Upload the completed template to TAP, review the calculated tax, then submit and pay."
    ],
    "notes": "Utah requires IFTA returns (TC-922) to be filed electronically; paper filing was phased out in 2021."
  },
  {
    "code": "VT",
    "name": "Vermont",
    "agency": "Vermont Department of Motor Vehicles (Commercial Vehicle Operations)",
    "portalName": "Vermont IRP & IFTA Payments",
    "portalUrl": "https://secure.vermont.gov/DMV/irp/",
    "infoUrl": "https://dmv.vermont.gov/CVO/fuel-tax/international-fuel-tax-agreement-ifta",
    "steps": [
      "Complete the quarterly IFTA return form the DMV sends before each due date, listing miles and fuel per jurisdiction.",
      "Submit the signed return to VT DMV Commercial Vehicle Operations by mail or in person.",
      "Pay any tax due, using the Vermont.gov online IRP/IFTA payment service if paying by card."
    ],
    "notes": "Vermont has no full e-file system for the quarterly return; the online portal handles payments and trip permits while the return itself is filed on paper."
  },
  {
    "code": "VA",
    "name": "Virginia",
    "agency": "Virginia Department of Motor Vehicles (DMV)",
    "portalName": "VIIM System",
    "portalUrl": "https://viim.dmv.virginia.gov/",
    "infoUrl": "https://www.dmv.virginia.gov/businesses/motor-carriers/ifta/tax",
    "steps": [
      "Log in to your VIIM System account with your Virginia IFTA account credentials.",
      "Enter your miles and fuel purchases per jurisdiction, or import them from a CSV file.",
      "Let VIIM calculate the tax, then review, submit, and pay the return online."
    ],
    "notes": "VIIM accepts CSV imports, so you can upload data exported from your ELD or fuel-tracking software."
  },
  {
    "code": "WA",
    "name": "Washington",
    "agency": "Washington State Department of Licensing (DOL)",
    "portalName": "License Express",
    "portalUrl": "https://secure.dol.wa.gov/home/",
    "infoUrl": "https://dol.wa.gov/vehicles-and-boats/prorate-and-fuel-tax/international-fuel-tax-agreement-ifta",
    "steps": [
      "Log in to License Express and open your IFTA account.",
      "Choose View Returns, find the quarter, and select File Now (or import your data from the Excel template).",
      "Review the calculated tax, then submit the return and pay online."
    ],
    "notes": "License Express replaced Washington's old Taxpayer Access Point (TAP); returns are due by 5:30 p.m. Pacific on the due date."
  },
  {
    "code": "WV",
    "name": "West Virginia",
    "agency": "West Virginia Tax Division",
    "portalName": "MyTaxes",
    "portalUrl": "https://mytaxes.wvtax.gov/_/",
    "infoUrl": "https://tax.wv.gov/Business/MotorFuel/InternationalFuelTaxAgreement/Pages/InternationalFuelTaxAgreementHowDoI.aspx",
    "steps": [
      "Log in to MyTaxes and open your IFTA account.",
      "Enter the quarter's miles traveled and fuel purchased for each jurisdiction (form IFTA-13).",
      "Review the calculated tax, then submit the return and pay electronically."
    ],
    "notes": "IFTA is run by the state Tax Division rather than the DMV, so it is filed in the same MyTaxes portal as other West Virginia business taxes."
  },
  {
    "code": "WI",
    "name": "Wisconsin",
    "agency": "Wisconsin Department of Transportation, DMV Motor Carrier Services",
    "portalName": "WisCRS (Wisconsin Carrier Registration System)",
    "portalUrl": "https://trust.dot.state.wi.us/wiscrs",
    "infoUrl": "https://wisconsindot.gov/Pages/dmv/com-drv-vehs/mtr-car-trkr/ifta.aspx",
    "steps": [
      "Log in to WisCRS with your Wisconsin WAMS user ID.",
      "Open your IFTA account and enter the quarter's miles and fuel purchases by jurisdiction.",
      "Let the system calculate the tax, then submit the return and pay online."
    ],
    "notes": "WisCRS access requires a free Wisconsin WAMS ID plus an approved system access request form."
  },
  {
    "code": "WY",
    "name": "Wyoming",
    "agency": "Wyoming Department of Transportation (WYDOT), Motor Vehicle Services",
    "portalName": "CCOWS (Commercial Carriers of Wyoming System)",
    "portalUrl": "https://ccows.dot.state.wy.us/WYEnterprise/",
    "infoUrl": "https://dot.state.wy.us/home/trucking_commercial_vehicles/ifta.html",
    "steps": [
      "Log in to CCOWS from the WYDOT trucking page.",
      "Open the IFTA tax return application and enter your quarterly miles and fuel by jurisdiction.",
      "Submit the return in CCOWS and pay any tax due."
    ],
    "notes": "WYDOT will not accept emailed or faxed quarterly returns; file through CCOWS or mail the paper form."
  }
];
